import { useAtom, useStore } from "jotai";
import { matchPath } from "react-router-dom";
import { routeToUrl } from "@CommonUtils/routes";
import { fetchMerchflowsReviews } from "src/api/merchflows/reviews";
import { useToasts } from "src/components/Toasts";
import { useUser } from "src/atoms/user";
import { useModals } from "src/components/Modals";
import { pages } from "src/utils";
import { useNavigation } from "src/utils/navigation";
import { useTranslation } from "src/i18n";
import { tourSeenAtom, tourStartingAtom, tourStateAtom } from "./atoms";
import { noPlanogramSteps, planogramExerciseSteps } from "./steps";
import { TourSeenStatus } from "./types";

/** How far back to look for a flow that already has a base planogram. */
const FLOWS_TO_SEARCH = 50;

/**
 * A flow created without a PSA has a base planogram row and nothing on it.
 * The list's type only declares `option` on the config, so this reads the
 * field the backend also stores there without claiming more than that.
 */
const hasNoPlanogramUploaded = (config: unknown): boolean =>
  typeof config === "object" &&
  config !== null &&
  "generationCdtSource" in config &&
  config.generationCdtSource === "STORE_PRODUCT";

export const useTour = () => {
  const [state, setState] = useAtom(tourStateAtom);
  const [seen, setSeen] = useAtom(tourSeenAtom);
  const store = useStore();
  const { user } = useUser();
  const { toast } = useToasts();
  const { navigate } = useNavigation();
  const { closeModal } = useModals();
  const { t } = useTranslation("common");

  const userKey = String(user?.id ?? "");
  const hasBeenOffered = seen[userKey] !== undefined;

  const markSeen = (status: TourSeenStatus) => {
    // A finished exercise stays finished, however the next one ends.
    if (seen[userKey] === "completed") return;
    void setSeen({ ...seen, [userKey]: status });
  };

  const start = async () => {
    // Already looking at a base planogram: the exercise runs on that one.
    const isOnBasePlanogram =
      matchPath(pages.merchflows_merchflowId_BasePog_basePogId_, window.location.pathname) !== null;
    if (isOnBasePlanogram) {
      markSeen("dismissed");
      setState({ steps: planogramExerciseSteps, stepIndex: 0, direction: 1 });
      return;
    }

    if (store.get(tourStartingAtom) !== null) return;
    const attempt = Symbol("tour start");
    store.set(tourStartingAtom, attempt);

    let flows;
    try {
      flows = await fetchMerchflowsReviews({
        page: 1,
        size: FLOWS_TO_SEARCH,
        sortBy: "updated_at",
        sortDirection: "desc",
      });
    } catch (error) {
      if (store.get(tourStartingAtom) !== attempt) return;
      store.set(tourStartingAtom, null);
      // Not "you have no planogram": leave the offer in place to try again.
      toast({ title: t("tour.loadFlowsFailed"), error });
      return;
    }
    // Dismissed while the list was loading: the user said no, so nothing opens.
    if (store.get(tourStartingAtom) !== attempt) return;
    store.set(tourStartingAtom, null);

    markSeen("dismissed");
    const withBasePlanogram = flows.data.filter(
      (candidate) => candidate.latest_base_pog_id !== null,
    );
    // One with a planogram on it shows every part of the exercise; an empty one is the fallback.
    const flow =
      withBasePlanogram.find((candidate) => !hasNoPlanogramUploaded(candidate.config)) ??
      withBasePlanogram[0];

    if (!flow || flow.latest_base_pog_id === null) {
      navigate(pages.merchflows);
      setState({ steps: noPlanogramSteps, stepIndex: 0, direction: 1 });
      return;
    }

    navigate(
      routeToUrl(pages.merchflows_merchflowId_BasePog_basePogId_, {
        merchflowId: flow.id,
        basePogId: flow.latest_base_pog_id,
      }),
    );
    setState({ steps: planogramExerciseSteps, stepIndex: 0, direction: 1 });
  };

  const end = () => {
    if (!state) return;

    const isLastStep = state.stepIndex >= state.steps.length - 1;
    if (isLastStep && state.steps === planogramExerciseSteps) markSeen("completed");
    // The exercise opens the rules itself, so it does not leave them open behind it.
    closeModal("ModalStorePlanogramRules");
    // Every confirmation a step answers, not just the current one's: the poll can lag the click that opened it.
    new Set(state.steps.flatMap((step) => step.confirmation ?? [])).forEach(
      (confirmation) => void closeModal(confirmation),
    );
    setState(null);
  };

  const closeConfirmation = () => {
    const confirmation = state?.steps[state.stepIndex]?.confirmation;
    if (confirmation) void closeModal(confirmation);
  };

  const goTo = (stepIndex: number, direction: 1 | -1) => {
    if (!state) return;

    if (stepIndex < 0) return;
    closeConfirmation();
    if (stepIndex >= state.steps.length) {
      end();
      return;
    }

    setState({ ...state, stepIndex, direction });
  };

  return {
    state,
    step: state ? state.steps[state.stepIndex] : undefined,
    hasBeenOffered,
    start,
    end,
    dismissOffer: () => {
      store.set(tourStartingAtom, null);
      markSeen("dismissed");
    },
    next: () => state && goTo(state.stepIndex + 1, 1),
    back: () => state && goTo(state.stepIndex - 1, -1),
  };
};
